
import { audioCache } from '@/lib/LocalPersistentStore';

export class AudioBufferLoader {
  private static bufferCache: Record<string, AudioBuffer> = {};

  public static async load(ctx: AudioContext, source: string): Promise<AudioBuffer | null> {
    // 1. Check in-memory cache (fastest)
    if (this.bufferCache[source]) return this.bufferCache[source];

    try {
      let arrayBuffer: ArrayBuffer | null = null;
      
      // 2. Check Persistent IndexedDB Cache (Phase 6)
      // Only cache remote URLs, not base64 blobs (which are already in the config)
      const isRemote = source.startsWith('http') || source.startsWith('/');
      if (isRemote) {
        arrayBuffer = await audioCache.get(source);
      }

      if (arrayBuffer) {
        // console.log(`[Phase 6] Audio Cache Hit: ${source.substring(0, 50)}...`);
      } else {
        // 3. Fetch from network or parse base64
        if (isRemote) {
          const resp = await fetch(source);
          if (!resp.ok) {
            if (resp.status === 403) {
              try {
                const errorData = await resp.json();
                if (errorData && errorData.error) {
                  throw new Error(`403 Forbidden - ${errorData.error}: ${errorData.message}`);
                }
              } catch (e) {
                // Fallback if not JSON or parsing fails
              }
              throw new Error("403 Forbidden - Token possibly expired or Asset Pending Security Scan");
            }
            throw new Error(`HTTP ${resp.status}`);
          }
          arrayBuffer = await resp.arrayBuffer();
          // Store in persistent cache
          await audioCache.set(source, arrayBuffer);
        } else {
          // Base64 handling
          const base64Data = source.includes(',') ? source.split(',')[1] : source;
          const bin = atob(base64Data);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          arrayBuffer = bytes.buffer;
        }
      }

      const buf = await ctx.decodeAudioData(arrayBuffer!);
      this.bufferCache[source] = buf;
      return buf;
    } catch (e) {
      console.error("Audio decode failed", e);
      return null;
    }
  }

  public static getFromCache(source: string): AudioBuffer | null {
      return this.bufferCache[source] || null;
  }
}
